import { useEffect, useMemo, useRef, useState } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { api } from '../../lib/api';
import { useEventImageUrl } from '../../lib/image-sas';
import { LoadingBlock } from '../../components/Spinner';
import WebsiteSourceUpdates from '../../components/WebsiteSourceUpdates';
import { formatRange, isAllDayEvent } from '../../lib/eventDates';

type IngestionStatus = 'pending' | 'extracted' | 'skipped' | 'poisoned' | 'failed';

interface ExtractedSummary {
  rowKey: string;
  title: string;
  startUtc: string;
  endUtc?: string;
  isAllDay?: boolean;
  confidence: number;
  status: string;
  venueName?: string;
  locationText?: string;
  eventType?: string;
  organizer?: string;
  isFreeEntry?: boolean;
  priceInfo?: string;
  ageRestriction?: string;
  isConfirmedEvent?: boolean;
  tags?: string[];
  performers?: string[];
  ticketUrl?: string;
  imageUrl?: string;
  seriesId?: string;
  recurringPattern?: string;
  isRecurringInstance?: boolean;
  duplicateOf?: string;
  processedAt?: string;
  updatedAt?: string;
  rejectReason?: string;
}

interface Ingestion {
  partitionKey: string;
  rowKey: string;
  fetchedAt: string;
  postedAt?: string;
  sourceType: string;
  sourceId: string;
  capturedByUser?: string;
  rawUrl?: string;
  rawText?: string;
  rawImageBlobUrl?: string;
  rawImageBlobUrls?: string[];
  hint?: string;
  processed: boolean;
  status: IngestionStatus;
  poisoned: boolean;
  poisonDequeueCount?: number;
  autoRetryCount?: number;
  lastError?: string;
  lastErrorAt?: string;
  lastErrorMessage?: string;
  extracted?: ExtractedSummary;
}

interface IngestionsResponse {
  items: Ingestion[];
  total: number;
  page: number;
  pageSize: number;
  statusCounts: Record<IngestionStatus, number>;
  sourceTypes: string[];
  sort?: string;
}

type SortField = 'fetchedAt' | 'processedAt' | 'postedAt' | 'confidence';
type SortDir = 'asc' | 'desc';

const SORT_OPTIONS: { value: `${SortField}:${SortDir}`; label: string }[] = [
  { value: 'fetchedAt:desc', label: 'Ingested (newest first)' },
  { value: 'fetchedAt:asc', label: 'Ingested (oldest first)' },
  { value: 'processedAt:desc', label: 'Processed (newest first)' },
  { value: 'processedAt:asc', label: 'Processed (oldest first)' },
  { value: 'postedAt:desc', label: 'Source posted (newest first)' },
  { value: 'postedAt:asc', label: 'Source posted (oldest first)' },
  { value: 'confidence:desc', label: 'Confidence (high to low)' },
  { value: 'confidence:asc', label: 'Confidence (low to high)' }
];

const STATUS_META: Record<IngestionStatus, { label: string; badge: string; help: string }> = {
  pending: {
    label: 'Pending',
    badge: 'bg-secondary',
    help: 'Captured but not yet processed by the extractor.'
  },
  extracted: {
    label: 'Event extracted',
    badge: 'bg-success',
    help: 'LLM identified an event; row created in EventsExtracted.'
  },
  skipped: {
    label: 'Skipped (not an event)',
    badge: 'bg-warning text-dark',
    help: 'LLM ran but determined the content is not an event (or had no start time).'
  },
  poisoned: {
    label: 'Failed (poison)',
    badge: 'bg-danger',
    help: 'Extraction failed 5 times. Message is in the poison queue.'
  },
  failed: {
    label: 'Failed',
    badge: 'bg-danger',
    help: 'Terminal handler failure (e.g. Azure OpenAI content filter). Row is marked processed so it will not retry; see the error message for details.'
  }
};

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

function formatRelative(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const diffMs = Date.now() - d.getTime();
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d ago`;
  return d.toLocaleDateString();
}

function formatAbsolute(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}

function formatEventStart(event: ExtractedSummary, startUtc = event.startUtc): string {
  return formatRange({ startUtc, isAllDay: isAllDayEvent(event) }, {
    date: { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' },
  });
}

export default function AdminIngestionsPage() {
  const [data, setData] = useState<IngestionsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | IngestionStatus>('all');
  const [sourceType, setSourceType] = useState<string>('');
  const [sort, setSort] = useState<`${SortField}:${SortDir}`>('fetchedAt:desc');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [retrying, setRetrying] = useState<string | null>(null);
  const [sweeping, setSweeping] = useState(false);
  const [sweepMsg, setSweepMsg] = useState<string | null>(null);

  // Debounce free-text search so each keystroke doesn't refetch.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reset page when filters change.
  useEffect(() => {
    setPage(1);
  }, [filter, sourceType, sort, pageSize]);

  const reqIdRef = useRef(0);
  const load = useMemo(
    () => () => {
      const myReq = ++reqIdRef.current;
      setLoading(true);
      setErr(null);
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));
      if (filter !== 'all') params.set('status', filter);
      if (sourceType) params.set('sourceType', sourceType);
      if (search) params.set('q', search);
      params.set('sort', sort);
      api
        .get<IngestionsResponse>(`/api/mgmt/ingestions?${params.toString()}`)
        .then((res) => {
          // Drop stale responses if the user changed filters quickly.
          if (reqIdRef.current === myReq) setData(res);
        })
        .catch((e) => {
          if (reqIdRef.current === myReq) setErr(String(e));
        })
        .finally(() => {
          if (reqIdRef.current === myReq) setLoading(false);
        });
    },
    [page, pageSize, filter, sourceType, search, sort]
  );

  useEffect(() => {
    void load();
  }, [load]);

  const sweepPoisoned = async () => {
    setSweeping(true);
    setSweepMsg(null);
    try {
      const res = await api.post<{
        scanned: number;
        reEnqueued: number;
        capped: number;
        missingRaw: number;
        malformed: number;
      }>('/api/mgmt/ingestions/retry-poison');
      setSweepMsg(
        `Swept ${res.scanned} poison message(s): ${res.reEnqueued} re-queued, ${res.capped} at retry cap, ${res.missingRaw} orphaned, ${res.malformed} malformed.`
      );
      await load();
    } catch (e) {
      setErr(`Sweep failed: ${String(e)}`);
    } finally {
      setSweeping(false);
    }
  };

  const retry = async (it: Ingestion) => {
    const key = `${it.partitionKey}|${it.rowKey}`;
    setRetrying(key);
    try {
      await api.post(
        `/api/mgmt/ingestions/${encodeURIComponent(it.partitionKey)}/${encodeURIComponent(it.rowKey)}/retry`
      );
      await load();
    } catch (e) {
      setErr(`Retry failed: ${String(e)}`);
    } finally {
      setRetrying(null);
    }
  };

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const counts = data?.statusCounts ?? { pending: 0, extracted: 0, skipped: 0, poisoned: 0, failed: 0 };
  const totalAll = counts.pending + counts.extracted + counts.skipped + counts.poisoned + counts.failed;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  return (
    <AdminLayout>
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h1 className="mb-0">Ingestions</h1>
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-outline-warning btn-sm"
            onClick={sweepPoisoned}
            disabled={sweeping || loading || (counts.poisoned ?? 0) === 0}
            title="Re-enqueue poisoned ingestions that haven't hit the auto-retry cap (also runs daily at 4am UTC)."
          >
            {sweeping ? 'Sweeping…' : `Sweep poisoned (${counts.poisoned ?? 0})`}
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={load} disabled={loading}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {sweepMsg && (
        <div className="alert alert-info py-2 small" role="status">
          {sweepMsg}
        </div>
      )}

      <p className="text-muted small">
        Each row is a captured submission (admin paste, browser extension, RSS feed, etc.). Status reflects what
        happened after the LLM extractor ran.
      </p>

      {err && (
        <div className="alert alert-danger" role="alert">
          {err}
        </div>
      )}

      <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
        <div className="btn-group flex-wrap" role="group" aria-label="Filter ingestions by status">
          <button
            type="button"
            className={`btn btn-sm ${filter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setFilter('all')}
            aria-pressed={filter === 'all'}
          >
            All ({totalAll})
          </button>
          {(Object.keys(STATUS_META) as IngestionStatus[]).map((s) => (
            <button
              key={s}
              type="button"
              className={`btn btn-sm ${filter === s ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setFilter(s)}
              aria-pressed={filter === s}
            >
              {STATUS_META[s].label} ({counts[s] ?? 0})
            </button>
          ))}
        </div>

        <label className="d-flex align-items-center gap-1 small mb-0">
          <span className="text-muted">Source:</span>
          <select
            className="form-select form-select-sm"
            value={sourceType}
            onChange={(e) => setSourceType(e.target.value)}
            aria-label="Filter by source type"
          >
            <option value="">All sources</option>
            {(data?.sourceTypes ?? []).map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>

        <label className="d-flex align-items-center gap-1 small mb-0">
          <span className="text-muted">Sort:</span>
          <select
            className="form-select form-select-sm"
            value={sort}
            onChange={(e) => setSort(e.target.value as `${SortField}:${SortDir}`)}
            aria-label="Sort ingestions"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>

        <label className="d-flex align-items-center gap-1 small mb-0 flex-grow-1" style={{ minWidth: 200 }}>
          <span className="visually-hidden">Search</span>
          <input
            type="search"
            className="form-control form-control-sm"
            placeholder="Search URL, title, venue, raw text…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            aria-label="Search ingestions"
          />
        </label>

        <label className="d-flex align-items-center gap-1 small mb-0">
          <span className="text-muted">Per page:</span>
          <select
            className="form-select form-select-sm"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            aria-label="Page size"
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
      </div>

      {loading && !data ? (
        <LoadingBlock label="Loading ingestions…" />
      ) : items.length === 0 ? (
        <p className="text-muted">No ingestions match this filter.</p>
      ) : (
        <>
          <div className="d-flex justify-content-between align-items-center small text-muted mb-2">
            <span>
              Showing <strong>{start.toLocaleString()}</strong>–<strong>{end.toLocaleString()}</strong> of{' '}
              <strong>{total.toLocaleString()}</strong>
            </span>
            <span>
              Page {page} of {totalPages}
            </span>
          </div>

          <ul className="list-group">
            {items.map((it) => (
              <IngestionRowItem
                key={`${it.partitionKey}|${it.rowKey}`}
                it={it}
                retry={retry}
                retrying={retrying === `${it.partitionKey}|${it.rowKey}`}
              />
            ))}
          </ul>

          <Pagination page={page} totalPages={totalPages} setPage={setPage} loading={loading} />
        </>
      )}
    </AdminLayout>
  );
}

function Pagination({
  page,
  totalPages,
  setPage,
  loading
}: {
  page: number;
  totalPages: number;
  setPage: (p: number) => void;
  loading: boolean;
}) {
  if (totalPages <= 1) return null;

  // Build a windowed list: 1 ... (page-1) page (page+1) ... totalPages
  const pages: (number | 'gap')[] = [];
  const window = 1;
  for (let p = 1; p <= totalPages; p++) {
    if (p === 1 || p === totalPages || Math.abs(p - page) <= window) {
      pages.push(p);
    } else if (pages[pages.length - 1] !== 'gap') {
      pages.push('gap');
    }
  }

  return (
    <nav aria-label="Ingestion pages" className="mt-3">
      <ul className="pagination pagination-sm mb-0 flex-wrap">
        <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
          <button
            type="button"
            className="page-link"
            onClick={() => setPage(page - 1)}
            disabled={page <= 1 || loading}
            aria-label="Previous page"
          >
            ‹ Prev
          </button>
        </li>
        {pages.map((p, idx) =>
          p === 'gap' ? (
            <li key={`gap-${idx}`} className="page-item disabled">
              <span className="page-link">…</span>
            </li>
          ) : (
            <li key={p} className={`page-item ${p === page ? 'active' : ''}`}>
              <button
                type="button"
                className="page-link"
                onClick={() => setPage(p)}
                disabled={loading}
                aria-label={`Page ${p}`}
                aria-current={p === page ? 'page' : undefined}
              >
                {p}
              </button>
            </li>
          )
        )}
        <li className={`page-item ${page >= totalPages ? 'disabled' : ''}`}>
          <button
            type="button"
            className="page-link"
            onClick={() => setPage(page + 1)}
            disabled={page >= totalPages || loading}
            aria-label="Next page"
          >
            Next ›
          </button>
        </li>
      </ul>
    </nav>
  );
}

function IngestionRowItem({
  it,
  retry,
  retrying
}: {
  it: Ingestion;
  retry: (it: Ingestion) => void;
  retrying: boolean;
}) {
  const meta = STATUS_META[it.status];
  const ex = it.extracted;
  const resolveImage = useEventImageUrl();
  const rawImages =
    it.rawImageBlobUrls && it.rawImageBlobUrls.length > 0
      ? it.rawImageBlobUrls
      : it.rawImageBlobUrl
        ? [it.rawImageBlobUrl]
        : [];
  const images = rawImages
    .map((u) => ({ raw: u, resolved: resolveImage(u) }))
    .filter((i): i is { raw: string; resolved: string } => !!i.resolved);
  const thumb = resolveImage(ex?.imageUrl) || images[0]?.resolved;

  return (
    <li className="list-group-item">
      <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
            <span className={`badge ${meta.badge}`} title={meta.help}>
              {meta.label}
            </span>
            <span className="badge bg-light text-dark border">{it.sourceType}</span>
            {it.poisoned && it.poisonDequeueCount != null && (
              <span className="small text-danger">{it.poisonDequeueCount} attempts</span>
            )}
            {it.lastError && (
              <span
                className="small text-danger"
                title={it.lastErrorMessage ?? ''}
              >
                {it.lastError}{it.lastErrorAt ? ` (${formatRelative(it.lastErrorAt)})` : ''}
              </span>
            )}
            {it.autoRetryCount != null && it.autoRetryCount > 0 && (
              <span
                className="small text-muted"
                title="Number of times the daily auto-retry timer has re-enqueued this ingestion."
              >
                auto-retried {it.autoRetryCount}×
              </span>
            )}
            <span className="small text-muted">
              <span className="text-muted-emphasis">Ingested </span>
              <time dateTime={it.fetchedAt} title={new Date(it.fetchedAt).toLocaleString()}>
                {formatAbsolute(it.fetchedAt)} ({formatRelative(it.fetchedAt)})
              </time>
            </span>
            {ex?.processedAt && (
              <span className="small text-muted">
                <span className="text-muted-emphasis">Processed </span>
                <time dateTime={ex.processedAt} title={new Date(ex.processedAt).toLocaleString()}>
                  {formatAbsolute(ex.processedAt)} ({formatRelative(ex.processedAt)})
                </time>
              </span>
            )}
            {it.postedAt && (
              <span
                className="small text-muted"
                title="When the original source post was published (e.g. Instagram takenAt). Used as the LLM's relative-date anchor."
              >
                <span className="text-muted-emphasis">Posted </span>
                <time dateTime={it.postedAt}>
                  {formatAbsolute(it.postedAt)} ({formatRelative(it.postedAt)})
                </time>
              </span>
            )}
            {it.capturedByUser && <span className="small text-muted">by {it.capturedByUser}</span>}
            {it.sourceId && (
              <span
                className="small text-muted text-truncate"
                style={{ maxWidth: 240 }}
                title={it.sourceId}
              >
                src: {it.sourceId}
              </span>
            )}
          </div>

          {it.rawUrl && (
            <p className="mb-1 small text-truncate">
              <a href={it.rawUrl} target="_blank" rel="noopener noreferrer">
                {it.rawUrl}
              </a>
            </p>
          )}

          {ex ? (
            <div className="mt-2 p-2 bg-light rounded small">
              <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                <strong>{ex.title}</strong>
                <span className="badge bg-info text-dark">{ex.status}</span>
                {ex.isConfirmedEvent === false && (
                  <span className="badge bg-warning text-dark" title="LLM marked this as not a confirmed event">
                    unconfirmed
                  </span>
                )}
                {ex.duplicateOf && (
                  <span className="badge bg-secondary" title={`Duplicate of ${ex.duplicateOf}`}>
                    duplicate
                  </span>
                )}
                {ex.recurringPattern && (
                  <span className="badge bg-light text-dark border">
                    {ex.isRecurringInstance ? 'instance' : 'series'}: {ex.recurringPattern}
                  </span>
                )}
                {ex.eventType && <span className="badge bg-light text-dark border">{ex.eventType}</span>}
                {ex.isFreeEntry && <span className="badge bg-success">Free</span>}
                {ex.ageRestriction && (
                  <span className="badge bg-light text-dark border">{ex.ageRestriction}</span>
                )}
              </div>
              <div className="text-muted">
                <time dateTime={ex.startUtc}>{formatEventStart(ex)}</time>
                {ex.endUtc && (
                  <>
                    {' – '}
                    <time dateTime={ex.endUtc}>{formatEventStart(ex, ex.endUtc)}</time>
                  </>
                )}
                {' · confidence '}
                {ex.confidence.toFixed(2)}
              </div>
              {(ex.venueName || ex.locationText) && (
                <div className="text-muted">
                  {ex.venueName ? <strong>{ex.venueName}</strong> : null}
                  {ex.venueName && ex.locationText ? ' · ' : null}
                  {ex.locationText}
                </div>
              )}
              {ex.organizer && <div className="text-muted">Organizer: {ex.organizer}</div>}
              {ex.priceInfo && <div className="text-muted">Price: {ex.priceInfo}</div>}
              {ex.rejectReason && (
                <div className="text-danger" title="Reason this row was rejected by the noise filter / judge / drift guard.">
                  Rejected: {ex.rejectReason}
                </div>
              )}
              {ex.performers && ex.performers.length > 0 && (
                <div className="text-muted text-truncate" title={ex.performers.join(', ')}>
                  Performers: {ex.performers.join(', ')}
                </div>
              )}
              {ex.tags && ex.tags.length > 0 && (
                <div className="d-flex flex-wrap gap-1 mt-1">
                  {ex.tags.map((t) => (
                    <span key={t} className="badge bg-light text-dark border">
                      #{t}
                    </span>
                  ))}
                </div>
              )}
              {ex.ticketUrl && (
                <div className="mt-1">
                  <a href={ex.ticketUrl} target="_blank" rel="noopener noreferrer" className="small">
                    Tickets ↗
                  </a>
                </div>
              )}
            </div>
          ) : it.rawText ? (
            <details className="mt-1">
              <summary className="small text-muted">Raw text</summary>
              <pre
                className="small mb-0 mt-1"
                style={{ whiteSpace: 'pre-wrap', maxHeight: '12em', overflow: 'auto' }}
              >
                {it.rawText}
              </pre>
            </details>
          ) : null}

          {it.hint && <p className="small text-muted mb-0 mt-1">Hint: {it.hint}</p>}
          {it.sourceType === 'website' && <WebsiteSourceUpdates rawPartitionKey={it.partitionKey} rawId={it.rowKey} />}

          {images.length > 1 && (
            <details className="mt-1">
              <summary className="small text-muted">{images.length} images</summary>
              <div className="d-flex flex-wrap gap-1 mt-1">
                {images.map(({ raw, resolved }, i) => (
                  <a key={`${raw}-${i}`} href={resolved} target="_blank" rel="noopener noreferrer">
                    <img
                      src={resolved}
                      alt=""
                      className="img-thumbnail"
                      style={{ maxHeight: 60, maxWidth: 60, objectFit: 'cover' }}
                    />
                  </a>
                ))}
              </div>
            </details>
          )}
        </div>

        {thumb && (
          <img
            src={thumb}
            alt=""
            className="img-thumbnail"
            style={{ maxHeight: 80, maxWidth: 80, objectFit: 'cover' }}
          />
        )}

        <div className="d-flex flex-column gap-1" style={{ minWidth: 100 }}>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => retry(it)}
            disabled={retrying}
            aria-label={`Retry extraction for ${it.rawUrl ?? it.rowKey}`}
            title={
              it.status === 'extracted'
                ? 'Re-run staged extraction; preserve prior events until a safe replacement completes'
                : 'Re-run the extractor'
            }
          >
            {retrying ? 'Retrying…' : 'Retry'}
          </button>
        </div>
      </div>
    </li>
  );
}
